// «Откуда берётся прибыль»: полоса A/B/C по доле прибыли и три блока групп; блок группы — фильтр списка блюд
import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../../styles/tokens';
import { ABC_LETTER_COLORS, LETTERS } from '../../utils/abcCalculations';
import { groupFilter, sameFilter, toggleFilter } from '../../utils/abcList';
import { money, percent, plural } from './abcLook';

const ACCENT = SECTION_ACCENTS['/cost'];
// Десктоп: A слева, B по центру, C справа — карточка широкая; на телефоне колонки узкие — всё влево
const DESKTOP_ALIGN = { A: 'left', B: 'center', C: 'right' };

export default function AbcDistributionCard({ result, filter, onFilter }) {
  const { groups, totals } = result;
  const showBar = totals.profit > 0;
  return (
    <Box component="section" aria-label="Откуда берётся прибыль" sx={{ ...CARD_SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        <Typography component="h3" sx={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>Откуда берётся прибыль</Typography>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
          {money(totals.profit)}
        </Typography>
      </Box>

      {showBar && (
        <Box aria-hidden="true" sx={{ display: 'flex', height: 12, mt: 1.5, borderRadius: '6px', overflow: 'hidden', gap: '2px' }}>
          {LETTERS.map((letter) => (
            <Box
              key={letter}
              sx={{
                width: `${Math.max(0, groups[letter].profitShare)}%`,
                backgroundColor: ABC_LETTER_COLORS[letter],
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 1, md: 2 }, mt: 1.75 }}>
        {LETTERS.map((letter) => {
          const group = groups[letter];
          const active = sameFilter(filter, groupFilter(letter));
          return (
            <ButtonBase
              key={letter}
              disabled={!group.dishes}
              onClick={() => onFilter(toggleFilter(filter, groupFilter(letter)))}
              aria-pressed={active}
              sx={{
                minWidth: 0,
                minHeight: 44,
                p: 1,
                m: -1,
                // Кнопка в сетке берёт ширину по содержимому — растягиваем на колонку (+ отрицательные поля)
                width: 'calc(100% + 16px)',
                borderRadius: '10px',
                display: 'block',
                textAlign: { xs: 'left', md: DESKTOP_ALIGN[letter] },
                boxShadow: active ? `inset 0 0 0 2px ${ABC_LETTER_COLORS[letter]}` : 'none',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' },
              }}
            >
              <Typography sx={{ fontSize: '13px', color: CARD_LABEL_COLOR }}>
                <Box component="span" sx={{ color: ABC_LETTER_COLORS[letter], fontWeight: 700 }}>{`Группа ${letter}`}</Box>
                {/* Телефон: «110 блюд» целиком второй строкой (иначе на ней оставалось одно «блюд»); десктоп — через запятую */}
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>, </Box>
                <Box component="span" sx={{ display: { xs: 'block', md: 'inline' }, whiteSpace: 'nowrap' }}>
                  {`${group.dishes} ${plural(group.dishes, ['блюдо', 'блюда', 'блюд'])}`}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: { xs: '20px', md: '24px' }, fontWeight: 800, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                {percent(group.profitShare)}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
                {`прибыли и ${percent(group.revenueShare)} выручки`}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}
